
import { Region, ChurchPresence, ChurchFunding, PeopleGroup } from '../types/region-types';
import { unreached_people_groups } from '../joshua-project';

/**
 * Get the appropriate color based on church presence.
 */
export const getRegionColor = (presence: ChurchPresence): string => {
  switch (presence) {
    case 'Good': return '#1EAEDB'; // Bright blue for good presence
    case 'Medium': return '#0FA0CE'; // Medium blue for medium presence
    case 'Low': return '#FFDEE2'; // Soft pink for low presence
    case 'Unreached': return '#ea384c'; // Reddish for unreached areas
    default: return '#6b7280'; // Gray for unknown
  }
};

/**
 * Calculate the total pledges for a region including church funding.
 */
export const getTotalRegionalPledges = (region: Region): number => {
  const churchFunding = getTotalChurchFunding(region);
  return (region.pledges || 0) + churchFunding;
};

/**
 * Calculate the total church funding for a region.
 */
export const getTotalChurchFunding = (region: Region): number => {
  if (!region.church_funding) return 0;
  return region.church_funding.reduce((total, church) => total + church.amount_raised, 0);
};

/**
 * Calculate the total unreached population.
 */
export const getTotalUnreachedPopulation = (regions: Region[]): number => {
  return regions.reduce((total, region) => total + (region.unreached_population || 0), 0);
};

/**
 * Get unreached people groups for a specific region
 */
export const getUnreachedGroupsByRegion = (regionId: string): PeopleGroup[] => {
  return unreached_people_groups.filter(group => group.region_id === regionId);
};

/**
 * Get the total count of unreached groups
 */
export const getTotalUnreachedGroups = (regions: Region[]): number => {
  return regions.reduce((total, region) => {
    const groups = getUnreachedGroupsByRegion(region.region_id);
    return total + groups.length;
  }, 0);
};

/**
 * Get unique primary languages in a region
 */
export const getPrimaryLanguagesInRegion = (regionId: string): string[] => {
  const groups = getUnreachedGroupsByRegion(regionId);
  const languages = groups.map(group => group.primary_language);
  return [...new Set(languages)];
};

/**
 * Get unique primary religions in a region
 */
export const getPrimaryReligionsInRegion = (regionId: string): string[] => {
  const groups = getUnreachedGroupsByRegion(regionId);
  const religions = groups.map(group => group.primary_religion);
  return [...new Set(religions)];
};

/**
 * Calculate funding percentage for a region
 */
export const getFundingPercentage = (pledges: number, targetNeed: number): number => {
  if (targetNeed === 0) return 0;
  const percentage = (pledges / targetNeed) * 100;
  return Math.min(percentage, 100); // Cap at 100%
};

/**
 * Get total pledges across all regions
 */
export const getTotalPledges = (regions: Region[]): number => {
  return regions.reduce((total, region) => total + (region.pledges || 0), 0);
};

/**
 * Get total missionaries across all regions
 */
export const getTotalMissionaries = (regions: Region[]): number => {
  return regions.reduce((total, region) => total + (region.missionaries || 0), 0);
};
